<script lang="ts" context="module">
  export const matchingProps = [];
</script>

<script lang="ts">
  import { writable } from 'svelte/store';
  import { apiCall } from '../utility/api';
  import ErrorInfo from '../elements/ErrorInfo.svelte';
  import hasPermission from '../utility/hasPermission';
  import { mountStorageConnectionPing } from '../utility/connectionsPinger';
  import ToolStripContainer from '../buttons/ToolStripContainer.svelte';

  import _ from 'lodash';
  import LoadingInfo from '../elements/LoadingInfo.svelte';
  import ToolStripButton from '../buttons/ToolStripButton.svelte';
  import FormProviderCore from '../forms/FormProviderCore.svelte';
  import { tick } from 'svelte';
  import Link from '../elements/Link.svelte';
  import { markTabSaved, markTabUnsaved } from '../utility/common';
  import invalidateCommands from '../commands/invalidateCommands';
  import createRef from '../utility/createRef';
  import stableStringify from 'json-stable-stringify';
  import { showModal } from '../modals/modalTools';
  import ConfirmModal from '../modals/ConfirmModal.svelte';
  import openNewTab from '../utility/openNewTab';
  import { getCurrentConfig } from '../stores';

  export let tabid;
  export let requiredPermission = null;
  export let adminLabel;
  export let apiCallList;
  export let apiCallGetDetail;
  export let apiCallSaveDetail;
  export let newObjectLabel;
  export let labelField;
  export let normalizeFormValues = null;
  export let pureTableName;

  let apiCallListKey = 0;

  mountStorageConnectionPing();

  let selectedObjectId = null;
  const loadedObjectDataRef = createRef(null);
  const selectedObjectStore = writable(null);

  async function loadSelectedObject(id) {
    if (id) {
      const data = await apiCall(apiCallGetDetail, { id });
      loadedObjectDataRef.set(data);
      $selectedObjectStore = data;
    } else {
      await tick();
      loadedObjectDataRef.set(null);
      $selectedObjectStore = null;
    }
  }

  $: loadSelectedObject(selectedObjectId);

  async function saveSelectedObject() {
    await apiCall(
      apiCallSaveDetail,
      normalizeFormValues ? normalizeFormValues($selectedObjectStore) : $selectedObjectStore
    );
    selectedObjectId = null;
  }

  function hasChanges() {
    return $selectedObjectStore && stableStringify($selectedObjectStore) != stableStringify(loadedObjectDataRef.get());
  }

  function requestCloseDetail() {
    if (hasChanges()) {
      showModal(ConfirmModal, {
        message: 'You have unsaved changes. Do you want to discard them?',
        onConfirm: () => {
          selectedObjectId = null;
        },
      });
    } else {
      selectedObjectId = null;
    }
  }

  $: {
    $selectedObjectStore;
    if (hasChanges()) {
      markTabUnsaved(tabid);
    } else {
      markTabSaved(tabid);
    }

    invalidateCommands();
  }
</script>

{#if !requiredPermission || hasPermission(requiredPermission)}
  <ToolStripContainer>
    {#if selectedObjectId}
      {#if $selectedObjectStore}
        <FormProviderCore values={selectedObjectStore}>
          <div class="wrapper">
            <h2>
              Administration &gt; <Link
                onClick={() => {
                  requestCloseDetail();
                }}>{adminLabel}</Link
              > &gt; {selectedObjectId == 'new'
                ? `(${newObjectLabel.toLowerCase()})`
                : _.isFunction(labelField)
                  ? labelField($selectedObjectStore)
                  : $selectedObjectStore[labelField]}
            </h2>
            <slot name="detail" values={$selectedObjectStore} {selectedObjectStore} />
          </div>
        </FormProviderCore>
      {:else}
        <LoadingInfo message={`Loading ${adminLabel.toLowerCase()} detail`} wrapper />
      {/if}
    {:else}
      {#await apiCall(apiCallList, { apiCallListKey })}
        <LoadingInfo message={`Loading ${adminLabel.toLowerCase()}`} wrapper />
      {:then rows}
        <div class="wrapper">
          <h2>Administration &gt; {adminLabel}</h2>
          <div class="flex1 scroll">
            <slot
              name="table"
              {rows}
              onSetSelectedObject={id => {
                selectedObjectId = id;
              }}
              onRefresh={() => {
                apiCallListKey++;
              }}
            />
          </div>
        </div>
      {/await}
    {/if}

    <svelte:fragment slot="toolstrip">
      {#if selectedObjectId}
        <ToolStripButton
          icon="icon arrow-left-bold"
          on:click={() => {
            requestCloseDetail();
          }}>Back to list</ToolStripButton
        >

        <ToolStripButton icon="icon save" on:click={saveSelectedObject}>Save</ToolStripButton>
      {/if}

      {#if !selectedObjectId}
        <ToolStripButton
          icon="icon refresh"
          on:click={() => {
            apiCallListKey++;
          }}>Refresh</ToolStripButton
        >

        <ToolStripButton
          icon="icon users"
          on:click={() => {
            selectedObjectId = 'new';
          }}>{newObjectLabel}</ToolStripButton
        >
      {/if}

      <ToolStripButton
        icon="icon table"
        on:click={() => {
          openNewTab({
            title: pureTableName,
            icon: 'img table',
            tabComponent: 'TableDataTab',
            props: {
              pureName: pureTableName,
              conid: '__storage',
              database: getCurrentConfig()?.storageDatabase,
              objectTypeField: 'tables',
            },
          });
        }}>Open table</ToolStripButton
      >
    </svelte:fragment>
  </ToolStripContainer>
{:else}
  <ErrorInfo message={`You don't have required permission: ${requiredPermission}`} icon="img warn" alignTop />
{/if}

<style>
  .wrapper {
    flex: 1;
    margin: 20px;
    margin-top: 0;
    display: flex;
    flex-direction: column;
  }
</style>
